// OneSignal Phase 3/4 — preserve the intended destination through the login
// flow, so a notification tap (or any deep link) that lands with an expired
// session still reaches the right conversation after re-authentication.
//
// Mechanism: sessionStorage stash, matching how the OTP flow already threads
// state between pages (padpulse_auth_email et al). AppLayout stashes the
// current in-app URL when its auth query 401s and it bounces to /app/login;
// each login-completion branch that would otherwise land on /app/dashboard
// consumes the stash instead. Onboarding/approval branches deliberately do NOT
// consume it — the gauntlet keeps precedence — and the stash expires so an
// abandoned login can't redirect a much-later session somewhere stale.
//
// Shared-device safety: the stash records which user it was captured for
// (when known). Consume sites pass the user who just authenticated; a
// mismatch discards the stash, so User B logging in after User A's session
// expired never inherits A's pending destination. Explicit logout clears the
// stash outright (clearReturnTo in every logout mutation).
//
// SECURITY: only internal /app/* paths are ever accepted, validated BOTH at
// stash and at consume time (sessionStorage is same-origin-writable by any
// code, so consume-side validation is the one that counts). This forecloses
// open redirects: no absolute URLs, no protocol-relative "//host", no
// backslash tricks, and never an auth/onboarding-gauntlet page (login-loop
// guard; list mirrors PUBLIC_AUTH_ROUTES in App.tsx, plus /app/no-team).

const KEY = "padpulse:auth:return-to";
const MAX_AGE_MS = 15 * 60 * 1000;
const MAX_LENGTH = 2000;

// Auth/gauntlet/system pages that must never be a post-login destination —
// restoring them would loop or dead-end the flow. Mirrors PUBLIC_AUTH_ROUTES
// (client/src/App.tsx) plus the no-team dead end.
const BLOCKED_PREFIXES = [
  "/app/login",
  "/app/verify",
  "/app/totp-verify",
  "/app/totp-enroll",
  "/app/awaiting-approval",
  "/app/onboarding",
  "/app/welcome",
  "/app/accept-invitations",
  "/app/no-team",
];

/** True only for a safe internal app path (optionally with query/hash). */
export function isSafeReturnTo(path: unknown): path is string {
  if (typeof path !== "string") return false;
  if (path.length > MAX_LENGTH) return false;
  // Must be a relative in-app path. startsWith("/app/") also rejects
  // protocol-relative "//host/..." and absolute "https://..." forms.
  if (!path.startsWith("/app/")) return false;
  // Backslashes get normalized to "/" by some URL parsers — reject outright.
  if (path.includes("\\")) return false;
  const pathnameOnly = path.split(/[?#]/)[0];
  if (BLOCKED_PREFIXES.some((p) => pathnameOnly === p || pathnameOnly.startsWith(`${p}/`))) {
    return false;
  }
  return true;
}

/** Stash the intended destination before redirecting to login. `forUserId`
 * (when known — e.g. the retained auth/me user at capture time) binds the
 * stash to that user so nobody else can consume it. No-op for unsafe values
 * and when sessionStorage is unavailable. */
export function stashReturnTo(path: string, forUserId?: number | null): void {
  if (!isSafeReturnTo(path)) return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ path, at: Date.now(), userId: typeof forUserId === "number" ? forUserId : null }),
    );
  } catch {
    // Storage unavailable (private mode/quota) — losing returnTo just means
    // the login lands on the dashboard, the pre-Phase-3 behavior.
  }
}

/** Read, validate, and clear the stashed destination. `forUserId` is the user
 * who just authenticated (when known); a stash bound to a DIFFERENT user is
 * discarded. Returns null when nothing valid is stashed (callers fall back to
 * /app/dashboard). */
export function consumeReturnTo(forUserId?: number | null): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    if (raw !== null) sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown; userId?: unknown };
    if (typeof parsed.at !== "number" || Date.now() - parsed.at > MAX_AGE_MS) return null;
    if (
      typeof parsed.userId === "number" &&
      typeof forUserId === "number" &&
      parsed.userId !== forUserId
    ) {
      return null;
    }
    return isSafeReturnTo(parsed.path) ? parsed.path : null;
  } catch {
    return null;
  }
}

/** Drop any pending destination. Called from every explicit logout so a
 * shared device never carries the previous user's deep link into the next
 * login. */
export function clearReturnTo(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
}

/** A purchase continuation precedes ordinary setup, but never security checks. */
export function consumeLifetimeReturnTo(forUserId?: number | null): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    const path = raw ? JSON.parse(raw)?.path : null;
    return typeof path === "string" && path.startsWith("/app/lifetime-claim?")
      ? consumeReturnTo(forUserId) : null;
  } catch { return null; }
}
